import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Loader, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import ENV from "config/Env";
import { ACL_ACTIVITIES, ACL_RESOURCES, DESK_COMMUNICATION_EVENTS, DMS_CAPABILITIES } from "models";
import { CarMileageModal } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/DeskCommunication/components/CounterTabletModal/CounterTabletModal.scss";
import { useCancelDeskCheckinMutation, useCreateDeskCheckinMutation } from "modules/AppointmentDetails/components/DeskCommunication/hooks";
import { useAppointmentCarInShopMutation, useAppointmentCarOutOfShopMutation, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { formatCurrentUTCDate, toISOString } from "utils/dateHelperFunctions";

import type { Appointment } from "models";

enum DESK_STATUS {
  Loading = "loading",
  DeskCheckOutSending = "desk_check_out_sending",
  DeskCheckinSending = "desk_checkin_sending",
  DeskCheckOutOpening = "desk_check_out_opening",
  DeskCheckinOpening = "desk_checkin_opening",
  DeskCheckOutFailedToOpen = "desk_check_out_failed_to_open",
  DeskCheckinFailedToOpen = "desk_checkin_failed_to_open",
  WaitingForAnswer = "waiting_for_answer",
  DeskCheckOutCanceled = "desk_check_out_canceled",
  DeskCheckinCanceled = "desk_checkin_canceled",
  UnknownErrorOccured = "unknown_error_occured",
  DeskCheckoutAnsweredCarIsOutOfShop = "desk_checkout_answered_car_is_out_of_shop",
  DeskCheckInAnsweredCarIsInShop = "desk_check_in_answered_car_is_in_shop",
  DeskCheckinAnswered = "desk_checkin_answered",
  DeskCheckOutAnswered = "desk_check_out_answered"
}

type DeskTabletModalProps = {
  appointment: Appointment;
  onClose: () => void;
};

export const CounterTabletModal = ({ appointment, onClose }: DeskTabletModalProps) => {
  const { t } = useTranslation();

  const [deskStatus, setDeskStatus] = useState(DESK_STATUS.Loading);
  const [receptionistKey, setReceptionistKey] = useState<string | null>(appointment.customer_communication?.receptionist_key || null);
  const [deskCommId, setDeskCommId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [showRetryButton, setShowRetryButton] = useState<boolean>(false);
  const [isCheckingOut, setIsCheckingOut] = useState<boolean>(false);
  const [currentMileage, setCurrentMileage] = useState<number | null>(null);
  const [isCarMileageModalOpen, setIsCarMileageModalOpen] = useState<boolean>(false);

  const canUpdateDeskCheckin = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DeskCheckin);
  const selectedLocation = useAppointmentLocation();

  const { mutateAsync: createDeskCheckin, isPending: isCreatePending } = useCreateDeskCheckinMutation(appointment.id);
  const { mutateAsync: cancelDeskCheckin, isPending: isCancelPending } = useCancelDeskCheckinMutation();
  const { mutateAsync: updateCarInShop, isPending: isCarInShopPending } = useAppointmentCarInShopMutation(appointment.id);
  const { mutateAsync: updateCarOutOfShop } = useAppointmentCarOutOfShopMutation(appointment.id);

  const handleCompleteDeskComm = async () => {
    setLoading(false);

    const shouldSetCarOutOfShopOnCheckout = appointment.car_in_shop && !appointment.car_out_of_shop && selectedLocation?.car_out_of_shop_at_desk_checkout_enabled;
    const shouldSetCarInShopOnCheckIn = !appointment.car_in_shop || appointment.car_out_of_shop;

    const isLocationWithNoMileage =
      selectedLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.WriteBackCarInShop, DMS_CAPABILITIES.WriteBackCarOutOfShop) && !appointment.current_km;

    if (isCheckingOut) {
      if (shouldSetCarOutOfShopOnCheckout) {
        await updateCarOutOfShop({ car_out_of_shop: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormatWithZ) });
        setDeskStatus(DESK_STATUS.DeskCheckoutAnsweredCarIsOutOfShop);
      } else setDeskStatus(DESK_STATUS.DeskCheckOutAnswered);
    } else {
      if (isLocationWithNoMileage) {
        setIsCarMileageModalOpen(true);
        return;
      }

      if (shouldSetCarInShopOnCheckIn) {
        await updateCarInShop({ car_in_shop: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormatWithZ) });
        setDeskStatus(DESK_STATUS.DeskCheckInAnsweredCarIsInShop);
      } else setDeskStatus(DESK_STATUS.DeskCheckinAnswered);
    }
  };

  useEffect(() => {
    if (!appointment.desk_communications?.length) return;

    const deskComm = [...appointment.desk_communications].sort((dc1, dc2) => dc2.id - dc1.id)[0];
    if (!deskComm.events?.length || !deskCommId || deskComm.id !== deskCommId) return;

    const event = [...deskComm.events].sort((e1, e2) => e2.id - e1.id)[0];

    setIsCheckingOut(deskComm.is_checking_out);
    setLoading(true);

    switch (event.type) {
      case DESK_COMMUNICATION_EVENTS.DeskCheckInSent:
        setDeskStatus(deskComm.is_checking_out ? DESK_STATUS.DeskCheckOutSending : DESK_STATUS.DeskCheckinSending);
        break;
      case DESK_COMMUNICATION_EVENTS.DeskCheckInReceived:
        setDeskStatus(deskComm.is_checking_out ? DESK_STATUS.DeskCheckOutOpening : DESK_STATUS.DeskCheckinOpening);
        break;
      case DESK_COMMUNICATION_EVENTS.DeskCheckInFailedToOpen:
        setDeskStatus(deskComm.is_checking_out ? DESK_STATUS.DeskCheckOutFailedToOpen : DESK_STATUS.DeskCheckinFailedToOpen);
        setLoading(false);
        setShowRetryButton(true);
        break;
      case DESK_COMMUNICATION_EVENTS.DeskCheckInOpened:
        setDeskStatus(DESK_STATUS.WaitingForAnswer);
        break;
      case DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered:
        handleCompleteDeskComm();
        break;
      case DESK_COMMUNICATION_EVENTS.DeskCheckInCanceled:
        setDeskStatus(deskComm.is_checking_out ? DESK_STATUS.DeskCheckOutCanceled : DESK_STATUS.DeskCheckinCanceled);
        setLoading(false);
        break;
      default:
        setDeskStatus(DESK_STATUS.UnknownErrorOccured);
        setShowRetryButton(true);
        setLoading(false);
        break;
    }
  }, [appointment.desk_communications]);

  const createDeskComm = async () => {
    setDeskStatus(DESK_STATUS.Loading);
    setShowRetryButton(false);

    try {
      const response = await createDeskCheckin();
      setDeskCommId(response.id);
      setReceptionistKey(response.receptionist_key);
    } catch {
      setDeskStatus(DESK_STATUS.UnknownErrorOccured);
      setShowRetryButton(true);
    }
  };

  useEffect(() => {
    createDeskComm();
  }, []);

  const handleCancel = async () => {
    try {
      await cancelDeskCheckin(deskCommId);
      setDeskCommId(null);
      onClose();
    } catch {
      setDeskStatus(DESK_STATUS.UnknownErrorOccured);
      onClose();
    }
  };

  const handleCarMileageConfirm = async () => {
    try {
      await updateCarInShop({ car_in_shop: toISOString(new Date()), current_km: currentMileage ?? 0 });
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const resetCarMileageModal = () => {
    setIsCarMileageModalOpen(false);
    setCurrentMileage(null);
    onClose();
  };

  const handleOpenDeskCheckin = () => {
    if (!receptionistKey) return;

    window.open(`${ENV.commBaseURL}/deskcheckin/#${receptionistKey}`, "_blank");
  };

  const isLoading = isCreatePending || isCancelPending || loading;
  const canCancelDeskCheckin = isLoading && canUpdateDeskCheckin;

  if (isCarMileageModalOpen) {
    return (
      <CarMileageModal
        showCarMileageModal={isCarMileageModalOpen}
        currentMileage={currentMileage}
        onSetCurrentMileage={setCurrentMileage}
        onResetCarMileageModal={resetCarMileageModal}
        onHandleCarMileageConfirm={handleCarMileageConfirm}
        title={
          !isCheckingOut
            ? t("v8_enter_current_km_to_set_car_in_shop", "Enter current km to set Car in shop")
            : t("v8_enter_current_km_to_set_car_out_of_shop", "Enter current km to set Car out of shop")
        }
        isLoading={isCarInShopPending}
      />
    );
  }

  return (
    <Modal className="CounterTabletModal" open closeOnDimmerClick={false} onClose={onClose} size="mini">
      <Modal.Header className="CounterTabletModal__header">
        {isCheckingOut ? t("v8_desk_check_out", "Desk check-out") : t("v8_desk_checkin", "Desk checkin")}
        {receptionistKey && (
          <Button icon basic size="large" onClick={handleOpenDeskCheckin}>
            <Icon className="external link" />
          </Button>
        )}
      </Modal.Header>

      <Modal.Content className="CounterTabletModal__content">
        {isLoading && <Loader inline="centered" active />}
        <h3>{t(`v8_${deskStatus}`) || deskStatus}</h3>
      </Modal.Content>

      <Modal.Actions className="CounterTabletModal__actions">
        {showRetryButton && (
          <Button color="green" onClick={createDeskComm}>
            {t("v8_retry", "Retry")}
            <Icon className="redo" />
          </Button>
        )}
        <Button color="red" onClick={canCancelDeskCheckin ? handleCancel : onClose}>
          {canCancelDeskCheckin ? t("v8_cancel", "Cancel") : t("v8_close", "Close")}
          <Icon className="xmark" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
